import React, { useState } from 'react';
import type { PiV2Bundle } from '../../patient-insurance/typesV2';
import { PI_V2_ROLE_LABEL, piV2IsInVisitWindow } from '../../patient-insurance/utilsV2';
import { Icon } from '../Icon/Icon';
import { PatientInsuranceAlerts } from './insurance-v2/PatientInsuranceAlerts';
import { PatientInsuranceBenefitsV2 } from './insurance-v2/PatientInsuranceBenefitsV2';
import { PatientInsuranceCallHistory } from './insurance-v2/PatientInsuranceCallHistory';
import {
  PatientInsuranceClaimsV2,
  PatientInsuranceTreatmentPlanV2,
} from './insurance-v2/PatientInsuranceMoneyV2';
import { PatientInsuranceNextVisit } from './insurance-v2/PatientInsuranceNextVisit';
import { PatientInsuranceNotes } from './insurance-v2/PatientInsuranceNotes';
import { PatientInsuranceOpportunities } from './insurance-v2/PatientInsuranceOpportunities';
import {
  PatientInsuranceAddPlanV2,
  PatientInsurancePlanCardV2,
} from './insurance-v2/PatientInsurancePlanCardV2';
import { PatientInsurancePlanDetail } from './insurance-v2/PatientInsurancePlanDetail';
import { PatientInsuranceVerificationSources } from './insurance-v2/PatientInsuranceVerificationSources';

export interface PatientInsuranceTabContentV2Props {
  bundle: PiV2Bundle;
  onReVerify?: (planId: string) => void;
  /** Plan id currently being re-verified (spec §8.5 in-progress state). */
  reverifyingPlanId?: string | null;
}

const NEXT_ROLE = ['primary', 'secondary', 'tertiary'] as const;

/**
 * Insurance, v2 — ordered by the question being asked.
 *
 * The desk arrives with one of four questions: are they covered, is there
 * anything they should use before it expires, what will this cost them, and
 * (afterwards) why did the last one cost what it did. The page answers them in
 * that order, which is why opportunities sit above the plan cards: knowing the
 * carrier's name is rarely the reason someone opened this tab.
 *
 * Two structural changes from v1. Plans are a list of 1–3 and every coverage
 * fact lives inside its plan, so nothing on screen is unattributed. And the
 * "Single card / Two cards" control is gone — the number of cards is the
 * number of plans, not a setting.
 */
export function PatientInsuranceTabContentV2({
  bundle,
  onReVerify,
  reverifyingPlanId,
}: PatientInsuranceTabContentV2Props) {
  const firstPlanId = bundle.plans[0]?.id ?? '';
  const [selectedPlanId, setSelectedPlanId] = useState(firstPlanId);

  const nextRole = NEXT_ROLE[bundle.plans.length];

  /**
   * The band is appointment-scoped and windowed. The backend should only send
   * `visit` when it is inside the window, but the check is repeated here so a
   * stale payload cannot put a past appointment at the top of the page.
   */
  const visit = bundle.visit && piV2IsInVisitWindow(bundle.visit.appointment) ? bundle.visit : null;

  if (bundle.plans.length === 0) {
    return (
      <div className="in-fade mx-auto w-full max-w-[1180px]">
        <div className="rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-5 py-[60px] text-center">
          <p className="m-0 text-[13.5px] text-[var(--ink-3)]">
            No insurance on file for this patient.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="in-fade mx-auto flex w-full max-w-[1180px] flex-col gap-4">
      {/* Opportunities and the estimate are one thing — what to raise at this
          visit, and what it costs — so they sit side by side and appear and
          disappear together. */}
      {visit ? (
        <div
          className="grid items-start gap-4"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))' }}
        >
          <PatientInsuranceOpportunities
            opportunities={visit.opportunities}
            appointment={visit.appointment}
          />
          <PatientInsuranceNextVisit
            estimate={visit.estimate}
            appointment={visit.appointment}
            {...(onReVerify && firstPlanId
              ? {
                  onReVerify: () => {
                    onReVerify(firstPlanId);
                  },
                }
              : {})}
          />
        </div>
      ) : null}

      {bundle.coordinationNote != null && bundle.plans.length > 1 ? (
        <div className="flex items-start gap-2.5 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--ai-3)_22%,transparent)] bg-[var(--ai-tint)] px-4 py-[11px]">
          <Icon kind="info" size={15} stroke={2} style={{ color: 'var(--ai-3)', marginTop: 1, flexShrink: 0 }} />
          <span className="text-[13px] leading-[1.5] text-[var(--ink-1)]">
            <strong>Coordination of benefits:</strong> {bundle.coordinationNote}
          </span>
        </div>
      ) : null}

      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))' }}
      >
        {bundle.plans.map((plan) => (
          <PatientInsurancePlanCardV2
            key={plan.id}
            plan={plan}
            {...(onReVerify ? { onReVerify } : {})}
            verifying={reverifyingPlanId != null && plan.id === reverifyingPlanId}
          />
        ))}
        {nextRole ? (
          <PatientInsuranceAddPlanV2 nextRole={PI_V2_ROLE_LABEL[nextRole].toLowerCase()} />
        ) : null}
      </div>

      {/* ELVA insurance alerts (spec §9) — risks/blockers/missing data, sitting
          directly under the plan cards so anything that blocks coverage is seen
          before the benefit numbers below it. */}
      <PatientInsuranceAlerts alerts={bundle.alerts ?? []} />

      <PatientInsuranceBenefitsV2 plans={bundle.plans} />

      {/* Treatment Plan Coverage sits immediately after Benefits Remaining — it
          explains the insurance impact across all pending treatment (spec §11.1). */}
      <PatientInsuranceTreatmentPlanV2 plan={bundle.treatmentPlan} />

      <PatientInsurancePlanDetail
        plans={bundle.plans}
        selectedPlanId={selectedPlanId}
        onSelectPlan={setSelectedPlanId}
      />

      {/* The evidence layer (spec §14–§16): what was checked, and the call trail
          behind it. Both render nothing when empty. */}
      <PatientInsuranceVerificationSources sources={bundle.verificationSources ?? []} />

      <PatientInsuranceCallHistory calls={bundle.calls ?? []} />

      <PatientInsuranceClaimsV2 claims={bundle.claims} />

      {/* Human-entered insurance context (spec §18) — always labelled as manual,
          renders nothing when empty. */}
      <PatientInsuranceNotes notes={bundle.notes ?? []} />

      <p className="m-0 mt-1 text-[11px] leading-[1.55] text-[var(--ink-4)]">
        Insurance benefits are estimates and not a guarantee of payment. Final benefits are
        determined by the payer after claim adjudication. Patient responsibility may change based on
        eligibility, remaining maximums, deductibles, coordination of benefits, plan limitations, and
        services received elsewhere.
      </p>

      <div className="h-12 shrink-0" aria-hidden />
    </div>
  );
}
